import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { API_BASE_URL } from '../api-base-url.token';

export interface QueueBrief {
  id: string;
  name: string;
  status?: 'OPEN' | 'CLOSED';
}

export interface ApiQueueView extends QueueBrief {
  averageTodayMin?: number;      // Moyenne manuelle du jour (backend actuel)
  communicatedWaitMin?: number;  // (si exposé côté back)
  level?: number;
  lastUpdatedAt?: string;
}

export interface NowResponse {
  minutes: number;
  source: 'AUTO' | 'MANUAL' | string;
  last_update: string; // ISO 8601 (on garde snake_case pour compat)
  thresholds: { low: number; medium?: number; high: number }; // medium optionnel pour compat
  open: boolean;
  degraded: boolean;
  confidence?: '●' | '●●' | '●●●';
  confidence_level?: 1 | 2 | 3;
  why?: Record<string, unknown>;
  timezone?: string;

  // Champs mappés en camelCase pour l’UI
  totalQrScans?: number;
  patientSatisfaction?: number;
}

export interface ManualWaitResponse {
  minutes: number;
  source: 'MANUAL' | 'AUTO' | string;
  valid_until: string; // ISO 8601
}

export interface SetStatusResponse {
  id: string;
  status: 'OPEN' | 'CLOSED';
  patient_message?: string;
}

@Injectable({ providedIn: 'root' })
export class QueuesService {
  constructor(private http: HttpClient) {}

  /** Base d’URL dynamique selon le domaine (local/dev/prod). */
  private base = inject(API_BASE_URL);

  // Liste des files d’un établissement
  listByEstablishment(establishmentId: string): Observable<ApiQueueView[]> {
    const url = `${this.base}/api/v1/establishments/${encodeURIComponent(establishmentId)}/queues`;
    return this.http.get<ApiQueueView[]>(url);
    // Variante possible si besoin :
    // return this.http.get<ApiQueueView[]>(`${this.base}/api/v1/queues`, { params: { establishmentId } });
  }

  // Vue "now" (Auto/Manuel) + mapping snake_case -> camelCase pour les compteurs
  now(queueId: string): Observable<NowResponse> {
    const url = `${this.base}/api/v1/queues/${encodeURIComponent(queueId)}/now`;
    return this.http.get<any>(url).pipe(
      map((raw: any) => {
        const mapped: NowResponse = {
          minutes: raw?.minutes ?? 0,
          source: raw?.source ?? 'AUTO',
          last_update: raw?.last_update,
          thresholds: raw?.thresholds ?? { low: 10, high: 30 },
          open: !!raw?.open,
          degraded: !!raw?.degraded,
          confidence: raw?.confidence,
          confidence_level: raw?.confidence_level,
          why: raw?.why,
          timezone: raw?.timezone,
          // mapping des compteurs
          totalQrScans: raw?.totalQrScans ?? raw?.total_qr_scans ?? 0,
          patientSatisfaction: raw?.patientSatisfaction ?? raw?.patient_satisfaction ?? 0,
        };
        return mapped;
      })
    );
  }

  // Fixer l’attente manuellement
  setManualWait(queueId: string, minutes: number, cause?: string): Observable<ManualWaitResponse> {
    const url = `${this.base}/api/v1/queues/${encodeURIComponent(queueId)}/manual-wait`;
    const payload: any = { minutes };
    if (cause && cause.trim()) {
      payload.cause = cause.trim();
    }
    return this.http.post<ManualWaitResponse>(url, payload);
  }

  // Annuler la saisie manuelle
  clearManualWait(queueId: string): Observable<void> {
    const url = `${this.base}/api/v1/queues/${encodeURIComponent(queueId)}/manual-wait`;
    return this.http.delete<void>(url);
  }

  // Ouvrir/Fermer la file
  setStatus(queueId: string, status: 'OPEN' | 'CLOSED'): Observable<SetStatusResponse> {
    const url = `${this.base}/api/v1/queues/${encodeURIComponent(queueId)}`;
    return this.http.put<SetStatusResponse>(url, { status });
  }
}
